import {
  PhysicalCardStatus,
  PhysicalCardStatusReason,
} from 'src/models/creditCard.models'

// Card details a card is missing before physical-card enrollment can proceed.
// Generic (not issuer-specific) — see extend-api's MissingCardDetail. EX-40639.
export enum MissingCardDetail {
  CardNumber = 'CARD_NUMBER',
  Expiry = 'EXPIRY',
  Cvc = 'CVC',
}

/**
 * A physical card whose enrollment failed for a reason the user can recover from by re-confirming
 * their card details: NOT_APPLICABLE with a recoverable reason — the verification service rejected
 * the supplied details (CvsCardDetailsRejected, EX-40942), the legacy ambiguous
 * CvsVerificationFailed, or a generic UnknownFailure. Terminal reasons are excluded:
 * ISSUER_NOT_SUPPORTED, CVS_AUTH_DECLINED (issuer declined the verification auth) and
 * CVS_POSTAL_CODE_REJECTED (the flow cannot change the postal code); a null reason can't be told
 * apart from a terminal cause, so it is excluded too. Mirrors extend-api's
 * `PhysicalCardStatusReason.RECOVERABLE_FAILURES` (EX-40694 / EX-40942).
 *
 * This is only half of the guard: every caller must ALSO require non-empty `missingCardDetails`
 * before routing into the confirm flow. That non-emptiness encodes both "flag on" and "backend
 * deployed" — an old backend returns an empty set for these accounts, so the surfaces stay hidden,
 * byte-identical to today (see the deploy-order safety note in EX-40694).
 */
export const isRecoverableEnrollmentFailure = (physicalCard: {
  status?: PhysicalCardStatus | null
  statusReason?: PhysicalCardStatusReason | null
}): boolean =>
  physicalCard.status === PhysicalCardStatus.NotApplicable &&
  (physicalCard.statusReason ===
    PhysicalCardStatusReason.CvsVerificationFailed ||
    physicalCard.statusReason === PhysicalCardStatusReason.UnknownFailure ||
    physicalCard.statusReason ===
      PhysicalCardStatusReason.CvsCardDetailsRejected)

/**
 * EX-40942: the issuer declined the CVS verification authorization. Distinct from
 * `isRecoverableEnrollmentFailure` because re-confirming card details cannot clear it — the retry
 * surfaces must stay hidden and the page should say plainly that verification was declined.
 * Mirrors extend-api's `PhysicalCardStatusReason.CVS_AUTH_DECLINED` (excluded from
 * `RECOVERABLE_FAILURES`).
 */
export const isDeclinedEnrollmentFailure = (physicalCard: {
  status?: PhysicalCardStatus | null
  statusReason?: PhysicalCardStatusReason | null
}): boolean =>
  physicalCard.status === PhysicalCardStatus.NotApplicable &&
  physicalCard.statusReason === PhysicalCardStatusReason.CvsAuthDeclined

/**
 * EX-40942: the CVS verification rejected the postal code. The confirm-details flow does not
 * collect a postal code (the integrator sends an empty one), so a retry resubmits the same input;
 * terminal for the retry flow, with its own copy — this is not an issuer decline.
 */
export const isPostalCodeRejectedEnrollmentFailure = (physicalCard: {
  status?: PhysicalCardStatus | null
  statusReason?: PhysicalCardStatusReason | null
}): boolean =>
  physicalCard.status === PhysicalCardStatus.NotApplicable &&
  physicalCard.statusReason === PhysicalCardStatusReason.CvsPostalCodeRejected

/**
 * EX-40942: a failure that re-confirming card details cannot clear — an issuer decline or a rejected
 * postal code. The confirm flow must treat a synchronous response in this state as settled for the
 * attempt (close and refresh into the Account Settings blocked-state copy) rather than handing off
 * to the verify step, which would fire a second enrollment with the same details.
 */
export const isTerminalEnrollmentFailure = (physicalCard: {
  status?: PhysicalCardStatus | null
  statusReason?: PhysicalCardStatusReason | null
}): boolean =>
  isDeclinedEnrollmentFailure(physicalCard) ||
  isPostalCodeRejectedEnrollmentFailure(physicalCard)

/**
 * Whether AccountSettings should replace the whole page with the static CVS-failed blocked box.
 * Extracted so both arms are testable without a full-page render (EX-40694): the suppression is a
 * composition of `isRecoverableEnrollmentFailure` AND confirmable missing details, and testing only
 * the leaf predicate leaves the composition — the actual takeover decision — uncovered.
 *
 * `hasConfirmableMissingDetails` is the caller's `missingCardDetails.length > 0 && canConnect`,
 * which is false with the flag off or against a backend that doesn't populate `missingCardDetails`
 * — so the takeover stays byte-identical to pre-EX-40694 in both of those cases.
 */
export const shouldShowCvsVerificationFailedTakeover = (params: {
  isBlocked?: boolean
  physicalCard: {
    status?: PhysicalCardStatus | null
    statusReason?: PhysicalCardStatusReason | null
  }
  hasConfirmableMissingDetails: boolean
}): boolean =>
  !params.isBlocked &&
  !(
    isRecoverableEnrollmentFailure(params.physicalCard) &&
    params.hasConfirmableMissingDetails
  ) &&
  params.physicalCard.status === PhysicalCardStatus.NotApplicable &&
  params.physicalCard.statusReason ===
    PhysicalCardStatusReason.CvsVerificationFailed

/**
 * Physical-card statuses at which the 3DS / enroll step has *settled* — the verify step can stop
 * its spinner and hand control back to its caller. Two very different outcomes live here:
 *
 * - `ACTIVE`: enrollment is done.
 * - `NEEDS_CVS_VERIFICATION`: 3DS passed, but the CVS micro-auth (the EX code) is still
 *   outstanding. The card is NOT active yet — see `isActivePhysicalCardStatus`.
 *
 * EX-40942: callers used to treat this whole set as "success" and fired the "Your card is now
 * active" toast on NEEDS_CVS_VERIFICATION. Use `isActivePhysicalCardStatus` for anything that
 * asserts activation; use this only to decide the verify step is finished.
 */
export const isSettledPhysicalCardStatus = (
  status?: PhysicalCardStatus | null,
): boolean =>
  status === PhysicalCardStatus.Active ||
  status === PhysicalCardStatus.NeedsCVSVerification

/** The only physical-card status that means the card is actually active. */
export const isActivePhysicalCardStatus = (
  status?: PhysicalCardStatus | null,
): boolean => status === PhysicalCardStatus.Active
